/**
 * The mask selector root component handles routing logic including which page to display
 *  as well as form submission logic
 */

import React, { useState, useEffect } from 'react';

import { Box, Container } from '@mui/material';

import {
  useLocation,
  useParams,
  useSearchParams,
  useNavigate,
} from 'react-router-dom';

import getApiClient from 'ui_patient/api/OpenClient';

import FullPageSpinner from 'ui_patient/components/FullPageSpinner';

import Header from './Header';

import MaskSelectorToolStyles from 'ui_patient/pages/MaskSelectorTool/MaskSelectorToolStyles';

import Footer from 'ui_patient/pages/MaskSelectorTool/Footer';

import qs from 'qs';

import { useTranslation } from 'react-i18next';

import { allowSkipQuestions } from 'ui_patient/AppSettings';
import HeaderContentFooter from './HeaderContentFooter';
import ErrorPage from './ErrorPage';
import CompletePage from 'ui_patient/pages/MaskSelectorTool/CompletePage';

const validateToken = async token => {
  return getApiClient().get(`/tokens/${token}`);
};

const buildStepUrl = (
  routePrefixPath,
  step,
  token,
  mugshot_enabled,
  skip_questions,
  clientId
) => {
  let baseUrl = `/${routePrefixPath}/${step}`;

  // Start with client_id as it's required
  let stepUrl = `${baseUrl}?center_id=${clientId}`;

  // Add skip questions parameter if allowed
  if (allowSkipQuestions()) {
    stepUrl = `${stepUrl}&skipquestions=${skip_questions}`;
  }

  // Maintain any localization override that is set
  const queryParams = qs.parse(window.location.search.substring(1));
  if (queryParams.lang) {
    stepUrl = `${stepUrl}&lang=${queryParams.lang}`;
  }

  return stepUrl;
};

const parseBool = (value, defaultValue) => {
  return /^true$/i.test(value);
};

const pageControlsSideMarginsList = ['patient'];

const MaskSelectorTool = ({
  initialValues,
  customValidateTokenMethod = undefined,
  customSubmitLogic = undefined,
  routePrefixPath = 'm',
  mugshotModel = null,
  clientId, // Add clientId as a prop
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const { step } = useParams();
  const [searchParams] = useSearchParams();

  const doesPageControlSideMargins = pageControlsSideMarginsList.includes(step);

  let searchString = location.search;

  // When testing with the test application the search value is empty on
  // the first page, but we can derive it from the path requested.
  if (searchString === '' && location.pathname.includes('?')) {
    searchString = '?' + location.pathname.split('?')[1];
  }

  const query = new URLSearchParams(searchString);

  const token = location.state?.token;
  const mugshot_enabled = !!location.state?.usePhoto;
  const skip_questions = parseBool(query.get('skipquestions'));

  const initialTokenStatus = !!token ? 'loading' : 'unavailable';

  const [tokenStatus, setTokenStatus] = useState(initialTokenStatus);
  const [tokenData, setTokenData] = useState(null);

  const { t } = useTranslation('pages.MaskSelectorTool.MaskSelectorTool');
  const linkExpiredError = t('linkExpiredError');

  const scrollRef = React.createRef();
  const historyLocation = useLocation();
  const [, setLastHistoryLocation] = useState('');

  const { classes } = MaskSelectorToolStyles();

  useEffect(() => {
    if (!clientId) {
      setTokenStatus('invalid');
      return;
    }

    (async () => {
      if (
        (token &&
          location.pathname !== `/${routePrefixPath}/complete` &&
          !tokenData) ||
        (token && !(token && tokenData && tokenData.token === token))
      ) {
        try {
          const response =
            customValidateTokenMethod === undefined
              ? await validateToken(token)
              : await customValidateTokenMethod(token);

          let tokenData = response.data;
          tokenData.mugshot_enabled = mugshot_enabled;
          tokenData.skip_questions = skip_questions;
          tokenData.client_id = clientId; // Add clientId to tokenData

          setTokenData(tokenData);
          setTokenStatus('valid');
        } catch (e) {
          console.log(e, 'error');
          setTokenStatus('invalid');
        }
      }

      if (window.scroll) {
        window.scroll({
          top: 0,
          behavior: 'instant',
        });
      }

      if (scrollRef?.current && scrollRef.current.scrollIntoView) {
        scrollRef.current.scrollIntoView(true);
      }

      setLastHistoryLocation(historyLocation);
    })();
  }, [
    location,
    token,
    tokenData,
    customValidateTokenMethod,
    mugshot_enabled,
    routePrefixPath,
    skip_questions,
    historyLocation,
    clientId, // Add clientId to dependency array
  ]);

  const container_styles = doesPageControlSideMargins
    ? ` ${classes.appRoot}`
    : `${classes.appRoot} ${classes.appRootPadding}`;
  console.log(clientId);

  const handleReturnToHome = () => {
    // Clear the location state by navigating to the main URL with only client_id
    navigate(`/?center_id=${clientId}`, {
      replace: true, // Replace the current entry in history
      state: {}, // Clear any existing state
    });

    // Reset any component state if needed
    setTokenStatus(initialTokenStatus);
    setTokenData(null);
  };
  return (
    <Container
      className={container_styles}
      style={{ minHeight: '100svh' }}
      disableGutters
      ref={scrollRef}
    >
      <Box display="flex" flexDirection="column">
        {tokenStatus === 'valid' && (
          <HeaderContentFooter
            tokenStatus={tokenStatus}
            tokenData={tokenData}
            initialValues={initialValues}
            customSubmitLogic={customSubmitLogic}
            routePrefixPath={routePrefixPath}
            mugshotModel={mugshotModel}
            clientId={clientId} // Pass clientId to HeaderContentFooter
          />
        )}

        {tokenStatus !== 'valid' && (
          <Box sx={{ minHeight: '100svh' }}>
            <Header
              displayBackButton={false}
              handleReturnToHome={handleReturnToHome}
            />

            <Box flex={1}>
              {tokenStatus === 'loading' && (
                <Box
                  minHeight="100svh"
                  display="flex"
                  flexDirection="column"
                  position="relative"
                >
                  <FullPageSpinner color="secondary" />
                </Box>
              )}
              {(tokenStatus === 'invalid' || tokenStatus === 'unavailable') && (
                <ErrorPage handleReturnToHome={handleReturnToHome} />
              )}

            </Box>

            <Footer />
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default MaskSelectorTool;

export { allowSkipQuestions, buildStepUrl };
